import { useEffect, useState } from 'react';
import { FaChevronLeft } from 'react-icons/fa';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import secureLocalStorage from 'react-secure-storage';
import { PageSpinner } from '../../../components/spinner/Spinner';
import { config } from '../../../Constants';
import toast from 'react-hot-toast';
import { Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

const TradingPlan = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const baseURL = config.baseURL;

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    minAmount: '',
    maxAmount: '',
    duration: '',
    expectedProfit: '',
    features: '',
    isActive: true,
    order: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`${baseURL}/trading-plans/admin/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setData(data);
        setFormData({
          title: data.title,
          subtitle: data.subtitle,
          minAmount: data.minAmount,
          maxAmount: data.maxAmount || '',
          duration: data.duration,
          expectedProfit: data.expectedProfit,
          features: data.features.join('\n'),
          isActive: data.isActive,
          order: data.order || 0,
        });
        setLoading(false);
      } catch (error) {
        toast.error('Failed to load trading plan');
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        minAmount: Number(formData.minAmount),
        maxAmount: formData.maxAmount ? Number(formData.maxAmount) : null,
        duration: Number(formData.duration),
        expectedProfit: Number(formData.expectedProfit),
        order: Number(formData.order),
        features: formData.features.split('\n').filter((f) => f.trim() !== ''),
      };

      await axios.patch(`${baseURL}/trading-plans/admin/${id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success('Trading plan updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.msg || 'Failed to update trading plan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${baseURL}/trading-plans/admin/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success('Trading plan deleted');
      navigate('/dashboard/trading-plans');
    } catch (error) {
      toast.error(error.response?.data?.msg || 'Failed to delete plan');
      setDeleting(false);
      closeDeleteModal();
    }
  };

  if (loading) {
    return (
      <main className='content trading-plan-page'>
        <PageSpinner />
      </main>
    );
  }

  if (!data) {
    return (
      <main className='content trading-plan-page'>
        <div className='title-btn'>
          <Link to='/dashboard/trading-plans'>
            <FaChevronLeft />
          </Link>
          <h1 className='title'>Trading Plan Not Found</h1>
        </div>
      </main>
    );
  }

  return (
    <main className='content trading-plan-page'>
      <div className='title-btn'>
        <Link to='/dashboard/trading-plans'>
          <FaChevronLeft />
        </Link>
        <h1 className='title'>Edit Trading Plan</h1>
      </div>

      <div className='block'>
        <div className='title-block'>
          <h2 className='title'>Plan Details</h2>
          <h4 className='sub-title'>Update trading plan information</h4>
        </div>
        <hr />

        <form onSubmit={handleSubmit} className='trading-plan-form'>
          <div className='form-row'>
            <div className='form-control'>
              <label>Title</label>
              <input
                type='text'
                name='title'
                value={formData.title}
                onChange={handleChange}
                placeholder='e.g. Silver Plan'
                required
              />
            </div>

            <div className='form-control'>
              <label>Subtitle</label>
              <input
                type='text'
                name='subtitle'
                value={formData.subtitle}
                onChange={handleChange}
                placeholder='e.g. Essential Trading'
                required
              />
            </div>
          </div>

          <div className='form-row'>
            <div className='form-control'>
              <label>Min Amount ($)</label>
              <input
                type='number'
                name='minAmount'
                value={formData.minAmount}
                onChange={handleChange}
                placeholder='1000'
                required
              />
            </div>

            <div className='form-control'>
              <label>Max Amount ($) - Optional</label>
              <input
                type='number'
                name='maxAmount'
                value={formData.maxAmount}
                onChange={handleChange}
                placeholder='10000'
              />
            </div>
          </div>

          <div className='form-row'>
            <div className='form-control'>
              <label>Duration (days)</label>
              <input
                type='number'
                name='duration'
                value={formData.duration}
                onChange={handleChange}
                placeholder='30'
                required
              />
            </div>

            <div className='form-control'>
              <label>Expected Profit (%)</label>
              <input
                type='number'
                name='expectedProfit'
                value={formData.expectedProfit}
                onChange={handleChange}
                placeholder='15'
                required
              />
            </div>
          </div>

          <div className='form-control'>
            <label>Features (one per line)</label>
            <textarea
              name='features'
              value={formData.features}
              onChange={handleChange}
              placeholder='Access to basic trading algorithms.&#10;Portfolio tracking and analytics.&#10;Email support.'
              rows={5}
              required
            />
          </div>

          <div className='form-row'>
            <div className='form-control'>
              <label>Display Order</label>
              <input
                type='number'
                name='order'
                value={formData.order}
                onChange={handleChange}
                placeholder='0'
              />
            </div>

            <div className='form-control checkbox'>
              <label>
                <input
                  type='checkbox'
                  name='isActive'
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                Active (visible to users)
              </label>
            </div>
          </div>

          <div className='btn-group'>
            <button type='submit' className='btn-primary' disabled={submitting}>
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type='button'
              className='btn-danger'
              onClick={openDeleteModal}
            >
              Delete Plan
            </button>
          </div>
        </form>
      </div>

      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        title='Delete Trading Plan'
        centered
        className='confirm-modal'
      >
        <div className='confirm-content'>
          <p>
            Are you sure you want to delete <strong>{data?.title}</strong>?
          </p>
          <p className='warning-text'>This action cannot be undone.</p>
          <div className='confirm-buttons'>
            <button className='btn-cancel' onClick={closeDeleteModal} disabled={deleting}>
              Cancel
            </button>
            <button
              className='btn-confirm btn-danger'
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>
    </main>
  );
};

export default TradingPlan;
